/**
 * Camp Hall-specific component showing character recruitment mechanics.
 * Styled for parchment theme.
 */
export function CampHallRecruitmentInfo({ level, stars, assignedCount }: { level: number; stars: number; assignedCount: number }) {
    // Constants matching the backend CampHallService
    const CHARACTERS_PER_LEVEL = 5;
    const CHARACTERS_PER_ASSIGNED = 2;
    const XP_PER_STAR = 50;

    // Calculate current capacity
    const baseCapacity = 1; // Celestial Moth
    const fromLevel = level * CHARACTERS_PER_LEVEL;
    const fromAssigned = assignedCount * CHARACTERS_PER_ASSIGNED;
    const totalCapacity = baseCapacity + fromLevel + fromAssigned;
    const bonusXp = stars * XP_PER_STAR;

    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">🏕️</span>
                <h2 className="text-xl font-serif text-amber-900">Character Recruitment</h2>
            </div>

            <p className="text-amber-800 font-serif text-sm mb-4 leading-relaxed">
                The Camp Hall allows you to recruit additional characters beyond your Celestial Moth.
                Higher levels and more assigned workers increase your recruitment capacity.
            </p>

            {/* Current Capacity */}
            <div className="bg-amber-100/40 rounded-lg p-4 mb-4 border border-amber-400/30">
                <h3 className="text-sm font-serif font-semibold text-green-800 uppercase tracking-wider mb-3">
                    Your Recruitment Capacity
                </h3>
                <div className="space-y-2 font-serif">
                    <div className="flex justify-between text-sm">
                        <span className="text-amber-700">Base (Celestial Moth)</span>
                        <span className="text-amber-900 font-medium">1</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-amber-700">From Level {level} ({level} × {CHARACTERS_PER_LEVEL})</span>
                        <span className="text-amber-900 font-medium">+{fromLevel}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-amber-700">From {assignedCount} Assigned ({assignedCount} × {CHARACTERS_PER_ASSIGNED})</span>
                        <span className="text-amber-900 font-medium">+{fromAssigned}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-amber-400/30 pt-2 mt-2">
                        <span className="text-green-700 font-semibold">Total Capacity</span>
                        <span className="text-green-700 font-bold">{totalCapacity} characters</span>
                    </div>
                </div>
            </div>

            {/* XP Bonus */}
            <div className="bg-amber-100/40 rounded-lg p-4 mb-4 border border-amber-400/30">
                <h3 className="text-sm font-serif font-semibold text-amber-800 uppercase tracking-wider mb-3">
                    New Character XP Bonus
                </h3>
                <p className="text-amber-700 font-serif text-sm mb-2">
                    Newly recruited characters start with bonus XP based on your Camp Hall's star rating.
                </p>
                <div className="flex items-center gap-3 font-serif">
                    <span className="text-amber-600">
                        {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
                    </span>
                    <span className="text-amber-900 font-bold text-lg">+{bonusXp} XP</span>
                </div>
            </div>

            {/* Capacity Breakdown Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Capacity by Level</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    Each level grants {CHARACTERS_PER_LEVEL} additional recruitment slots.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Base Slots</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Total (no workers)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${level === lvl ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-green-700">
                                        +{lvl * CHARACTERS_PER_LEVEL}
                                    </td>
                                    <td className="py-2 px-3 text-center text-amber-900 font-medium">
                                        {1 + lvl * CHARACTERS_PER_LEVEL}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* XP Bonus Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">XP Bonus by Stars</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    Higher star ratings grant more starting XP to newly recruited characters.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Stars</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Bonus XP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${stars === s ? 'bg-amber-200/40' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">
                                        {'★'.repeat(s)}{'☆'.repeat(5 - s)}
                                    </td>
                                    <td className="py-2 px-3 text-center text-amber-800 font-medium">
                                        +{s * XP_PER_STAR} XP
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Worker Bonus Info */}
            <div className="p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-serif font-semibold text-amber-800 uppercase mb-2">Worker Assignment Bonus</h4>
                <p className="text-xs font-serif text-amber-700">
                    Each character assigned to work at the Camp Hall increases your total recruitment capacity by {CHARACTERS_PER_ASSIGNED}.
                    With {stars} character slots available at your current star rating, you could gain up to +{stars * CHARACTERS_PER_ASSIGNED} additional slots.
                </p>
            </div>
        </div>
    );
}
